import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnnouncementComposeController } from 'fragments/announcements/interfaces';
import { useAPIAnnouncements } from 'services/announcements/api-announcements.service';
import { useMessenger } from 'tools/view-hooks/messenger-hook';
import { useTranslator } from 'tools/view-hooks/translator-hook';

export const useAnnouncementComposeController = (): AnnouncementComposeController => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { createAnnouncement, getAnnouncementReads } = useAPIAnnouncements();
    const messenger = useMessenger();
    const { translate } = useTranslator();

    const accessToken = searchParams.get('access_token') || '';
    const siteId = searchParams.get('site_id') || '';
    const isAdmin = searchParams.get('is_admin') === 'true';

    useEffect(() => {
        if (!accessToken || !siteId) {
            navigate('/mobile/404');
        }
    }, [accessToken, siteId]);

    const [title, setTitle] = useState<string>('');
    const [body, setBody] = useState<string>('');
    const [silent, setSilent] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [titleError, setTitleError] = useState<string | undefined>(undefined);

    const qs = `access_token=${accessToken}&site_id=${siteId}&is_admin=${isAdmin}`;

    const onTitle = (value: string) => {
        setTitle(value);
        if (titleError && value.trim()) {
            setTitleError(undefined);
        }
    };

    const onBody = (value: string) => setBody(value);

    const onToggleSilent = (value: boolean) => setSilent(value);

    const onPublish = () => {
        if (!title.trim()) {
            setTitleError(translate({ key: 'announcements.title-required' }));
            return;
        }
        setTitleError(undefined);
        if (!body.trim()) {
            return;
        }
        setError(undefined);
        setSubmitting(true);
        createAnnouncement(accessToken, { siteId, title, body, silent })
            .then((response) =>
                getAnnouncementReads(accessToken, response.id)
                    .then((stats) => stats.total)
                    .catch(() => 1)
                    .then((total) => {
                        messenger.showSuccessMessage({
                            key: 'announcements.published-toast',
                            extra: { count: total, noun: translate({ key: 'announcements.audience-noun' }) },
                        });
                        navigate(`/mobile/announcements?${qs}`);
                    }),
            )
            .catch(() => setError(translate({ key: 'announcements.publish-error' })))
            .finally(() => setSubmitting(false));
    };

    const onClose = () => navigate(`/mobile/announcements?${qs}`);

    return {
        state: { title, body, silent, submitting, error, titleError },
        events: { onTitle, onBody, onToggleSilent, onPublish, onClose },
    };
};
